import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import '../../Auth.css'
import AuthLeftBranding from '../AuthComponent/SignInProcess/AuthLeftBranding'
import AuthLandingFooter from '../AuthComponent/SignInProcess/AuthLandingFooter'
import LandingFaqAccordion from './LandingFaqAccordion'
import LandingWelcomeModal from './LandingWelcomeModal'
import { hasAuthToken, getDashboardPath } from '../../Utils/authStorage'

const CONTACT_EMAIL = 'famconnect26@aol.com'
const CONTACT_WEBMAIL_COMPOSE = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(CONTACT_EMAIL)}`
const SPLASH_SLOGAN = 'A Web App Created to Connect Family Members'
const SPLASH_SLOGAN_WORDS = SPLASH_SLOGAN.split(' ')
const SPLASH_WORD_COUNT = SPLASH_SLOGAN_WORDS.length
const SPLASH_WORD_START_MS = 1050
const SPLASH_WORD_STAGGER_MS = 130
const SPLASH_WORD_ANIM_MS = 620
const SPLASH_HOLD_MS = 1500
const SPLASH_EXIT_MS = 800
const SPLASH_INTRO_END_MS =
SPLASH_WORD_START_MS + (SPLASH_WORD_COUNT - 1) * SPLASH_WORD_STAGGER_MS + SPLASH_WORD_ANIM_MS
const SPLASH_EXIT_START_MS = SPLASH_INTRO_END_MS + SPLASH_HOLD_MS
const SPLASH_NAVIGATE_MS = SPLASH_EXIT_START_MS + SPLASH_EXIT_MS

const scrollHomeIntoView = () => {
  document.getElementById('home')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const Landing = () => {
  const [heroView, setHeroView] = useState('default')
  /** Tracks which route-style tab last gained focus (login/signup/dashboard); About/FAQ use heroView. */
  const [navFocus, setNavFocus] = useState(null)
  const isLoggedIn = hasAuthToken()
  const [showWelcomeModal, setShowWelcomeModal] = useState(false)
  const [splashExiting, setSplashExiting] = useState(false)
  const [splashKey, setSplashKey] = useState(0)
  const [aboutEntering, setAboutEntering] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const closeMobileNav = () => setMobileNavOpen(false)

  /** Phones open their mail app from mailto:. On desktop mailto: only works with a mail app set up,
   *  so if the browser never loses focus (nothing opened) fall back to a webmail compose screen. */
  const handleContactEmailClick = () => {
    if (window.matchMedia?.('(pointer: coarse)').matches) return
    let handled = false
    const onBlur = () => { handled = true }
    window.addEventListener('blur', onBlur, { once: true })
    setTimeout(() => {
      window.removeEventListener('blur', onBlur)
      if (!handled && document.hasFocus()) window.open(CONTACT_WEBMAIL_COMPOSE, '_blank', 'noopener')
    }, 600)
  }

  useEffect(() => {
    if (heroView !== 'default') return undefined

    setSplashExiting(false)

    const exitTimer = setTimeout(() => {
      setSplashExiting(true)
    }, SPLASH_EXIT_START_MS)

    const navigateTimer = setTimeout(() => {
      setHeroView((current) => {
        if (current !== 'default') return current
        if (!isLoggedIn) {
          setShowWelcomeModal(true)
        }
        return 'about'
      })
      setAboutEntering(true)
      setSplashExiting(false)
    }, SPLASH_NAVIGATE_MS)

    return () => {
      clearTimeout(exitTimer)
      clearTimeout(navigateTimer)
    }
  }, [heroView, splashKey, isLoggedIn])

  useEffect(() => {
    if (heroView !== 'about') {
      setAboutEntering(false)
      return undefined
    }

    setAboutEntering(true)
    const timer = setTimeout(() => setAboutEntering(false), 700)
    return () => clearTimeout(timer)
  }, [heroView])

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 900) setMobileNavOpen(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!mobileNavOpen) return undefined
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileNavOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [mobileNavOpen])

  const showAboutOrContact = (e) => {
    e.preventDefault()
    closeMobileNav()
    setNavFocus(null)
    setHeroView('about')
    if (!isLoggedIn) {
      setShowWelcomeModal(true)
    }
    scrollHomeIntoView()
  }

  const showFaq = (e) => {
    e.preventDefault()
    closeMobileNav()
    setNavFocus(null)
    setHeroView('faq')
    scrollHomeIntoView()
  }

  const backToDefault = () => {
    closeMobileNav()
    setNavFocus(null)
    setHeroView('default')
    setSplashKey((key) => key + 1)
  }

  const introClassName =
    'landing-intro' +
    (heroView === 'default' ? ' landing-intro--splash' : '') +
    (heroView === 'about' ? ' landing-intro--about' : '') +
    (heroView === 'faq' ? ' landing-intro--faq' : '')

  return (
    <div
      className={`landing-page-cs${mobileNavOpen ? ' landing-page-cs--nav-open' : ''}${
        heroView === 'about' ? ' landing-page-cs--about' : ''
      }${heroView === 'faq' ? ' landing-page-cs--faq' : ''}`}
    >
      <header className={`header${mobileNavOpen ? ' header--nav-open' : ''}`}>
        <div className="header-container">
          <div className="header-mobile-auth">
            {!isLoggedIn ? (
              <>
                <Link
                  to="/register"
                  className={`nav-tab header-mobile-auth-link${navFocus === 'signup' ? ' nav-tab--active' : ''}`}
                  onClick={() => {
                    closeMobileNav()
                    setNavFocus('signup')
                    setHeroView('default')
                  }}
                >
                  Sign Up (Admin Only)
                </Link>
                {/* FAQ sits between Sign Up and Login so users don't tap Sign Up by mistake */}
                <a
                  href="#home"
                  className={`nav-tab header-mobile-auth-link${heroView === 'faq' ? ' nav-tab--active' : ''}`}
                  onClick={showFaq}
                >
                  FAQ
                </a>
                <Link
                  to="/login"
                  className={`nav-tab header-mobile-auth-link${navFocus === 'login' ? ' nav-tab--active' : ''}`}
                  onClick={() => {
                    closeMobileNav()
                    setNavFocus('login')
                    setHeroView('default')
                  }}
                >
                  Login (All Users)
                </Link>
              </>
            ) : (
              <>
                <a
                  href="#home"
                  className={`nav-tab header-mobile-auth-link${heroView === 'faq' ? ' nav-tab--active' : ''}`}
                  onClick={showFaq}
                >
                  FAQ
                </a>
                <Link
                  to={getDashboardPath()}
                  className={`nav-tab header-mobile-auth-link${navFocus === 'dashboard' ? ' nav-tab--active' : ''}`}
                  onClick={() => {
                    closeMobileNav()
                    setNavFocus('dashboard')
                    setHeroView('default')
                  }}
                >
                  Dashboard
                </Link>
              </>
            )}
          </div>
          <button
            type="button"
            className={`nav-menu-toggle${mobileNavOpen ? ' nav-menu-toggle--open' : ''}`}
            aria-expanded={mobileNavOpen}
            aria-controls="landing-nav"
            aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            <span className="nav-menu-toggle-bar" aria-hidden />
            <span className="nav-menu-toggle-bar" aria-hidden />
            <span className="nav-menu-toggle-bar" aria-hidden />
          </button>
          <nav id="landing-nav" className={`nav${mobileNavOpen ? ' nav--open' : ''}`}>
            <a
              href="#home"
              className={`nav-tab${heroView === 'about' ? ' nav-tab--active' : ''}`}
              onClick={showAboutOrContact}
            >
              About the App / Contact
            </a>
            <a
              href="#home"
              className={`nav-tab nav-tab--drawer-faq${heroView === 'faq' ? ' nav-tab--active' : ''}`}
              onClick={showFaq}
            >
              FAQ
            </a>
            {!isLoggedIn && (
              <span className="nav-tab-tooltip-host nav-tab-tooltip-host--drawer-auth">
                <Link
                  to="/register"
                  className={`nav-tab${navFocus === 'signup' ? ' nav-tab--active' : ''}`}
                  onClick={() => {
                    closeMobileNav()
                    setNavFocus('signup')
                    setHeroView('default')
                  }}
                >
                  Sign Up (Admin Only)
                </Link>
                <span className="nav-tab-tooltip" role="tooltip">
                  Admin is responsible for capturing &amp; managing family data.
                </span>
              </span>
            )}
            <button type="button" className="nav-tab nav-tab--disabled nav-tab--family-news" disabled aria-disabled="true">
              Family News
            </button>
            <button type="button" className="nav-tab nav-tab--disabled" disabled aria-disabled="true">
              App News
            </button>
            {isLoggedIn ? (
              <Link
                to={getDashboardPath()}
                className={`nav-tab nav-tab--drawer-auth${navFocus === 'dashboard' ? ' nav-tab--active' : ''}`}
                onClick={() => {
                  closeMobileNav()
                  setNavFocus('dashboard')
                  setHeroView('default')
                }}
              >
                Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                className={`nav-tab nav-tab--drawer-auth${navFocus === 'login' ? ' nav-tab--active' : ''}`}
                onClick={() => {
                  closeMobileNav()
                  setNavFocus('login')
                  setHeroView('default')
                }}
              >
                Login (All Users)
              </Link>
            )}
          </nav>
        </div>
      </header>
      {mobileNavOpen && (
        <button
          type="button"
          className="nav-backdrop"
          aria-label="Close menu"
          onClick={closeMobileNav}
        />
      )}

      <main id="home" className={introClassName}>
        {heroView === 'default' && (
          <div
            key={splashKey}
            className={`landing-intro-splash${splashExiting ? ' landing-intro-splash--exiting' : ''}`}
            style={{ '--splash-idle-start': `${SPLASH_INTRO_END_MS}ms` }}
          >
            <button
              type="button"
              className="landing-intro-logo-wrap landing-intro-logo-wrap--home landing-intro-splash-logo"
              onClick={backToDefault}
              title="Return to welcome message"
            >
              <AuthLeftBranding showTagline={false} className="landing-intro-auth-logo" />
            </button>
            <p className="landing-intro-slogan landing-intro-splash-slogan" aria-label={SPLASH_SLOGAN}>
              {SPLASH_SLOGAN_WORDS.map((word, index) => (
                <span
                  key={`${splashKey}-${word}-${index}`}
                  className="landing-intro-splash-word"
                  style={{ '--word-i': index }}
                >
                  {word}
                </span>
              ))}
            </p>

            {/* <blockquote className="landing-intro-quote">
              <p>
                Having a place to go is home.
                <br />
                Having someone to love is family.
                <br />
                Having both is a blessing.
                <br />
                <br />- Donna Hedges
              </p>
            </blockquote> */}
          </div>
        )}

        {heroView === 'about' && (
          <div className={`landing-intro-about${aboutEntering ? ' landing-intro-about--enter' : ''}`}>
            <div className="landing-intro-about-top">
              <div className="landing-intro-about-brand-inner">
                <button
                  type="button"
                  className="landing-intro-logo-wrap landing-intro-logo-wrap--home landing-intro-logo-wrap--about"
                  onClick={backToDefault}
                  title="Return to welcome message"
                >
                  <AuthLeftBranding showTagline={false} className="landing-intro-auth-logo" />
                </button>
                <p className="landing-intro-slogan landing-intro-slogan--about">A Web App Created to Connect Family Members</p>
              </div>
            </div>

            <div className="landing-intro-about-body">
              <div className="landing-intro-about-content" aria-label="About FamConnect26">
                <section className="landing-intro-about-section">
                  <p>
                    FamConnect26 is a web app designed to help families stay connected, by providing an environment that
                    fosters an easy way for family members to have access to the entire family, in one place.
                  </p>
                </section>
                <section className="landing-intro-about-section">
                  <p>
                    Anyone in the family can share news, create events, and send text messages within the app.
                  </p>
                </section>
                <section className="landing-intro-about-section">
                  <p>
                    The app uses unique colors for each family line to help identify which family member is
                    associated with a particular line, making it easy to connect everyone to the heads of their line.
                  </p>
                </section>
                <section className="landing-intro-about-section">
                  <p className="landing-intro-benefits-heading">FamConnect26 offers the following benefits:</p>
                </section>
                <section className="landing-intro-about-section landing-intro-about-section--benefits">
                  <p className="landing-intro-benefits-subheading">Current</p>
                  <ul className="landing-about-benefits-list landing-about-benefits-list--current">
                    <li>Family Tree View (desktop version for now—mobile version coming soon)</li>
                    <li>Private Individual &amp; Group Text Messaging</li>
                    <li>Overview of Family Data Points in Real Time</li>
                    <li>DIY Flyer Invitation Creation</li>
                    <li>Import feature to add family data from Excel or CSV file</li>
                  </ul>
                  <p className="landing-intro-benefits-subheading">Upcoming</p>
                  <ul className="landing-about-benefits-list">
                    <li>AI Generated Flyer Designing</li>
                    <li>Ability to Share Family News</li>
                    <li>Customized Reporting Capability</li>
                  </ul>
                </section>
                <section className="landing-intro-about-section">
                  <p>
                    FamConnect26 encourages users to share the app with other families who want to create the same environment
                    for their family.
                  </p>
                </section>
                <section className="landing-intro-about-section">
                  <p>
                    If you have ideas you wish to see in this app, contact the FamConnect26 Team by sending an email to{' '}
                    <a
                      className="landing-intro-email-link"
                      href={`mailto:${CONTACT_EMAIL}`}
                      title={CONTACT_EMAIL}
                      onClick={handleContactEmailClick}
                    >
                      FamConnect26
                    </a>
                    .
                  </p>
                </section>
                <section className="landing-intro-about-section">
                  <p>
                    For more details, click{' '}
                    <button type="button" className="landing-intro-faq-link" onClick={showFaq}>
                      here
                    </button>{' '}
                    to view FAQ.
                  </p>
                </section>
              </div>
            </div>
          </div>
        )}

        {heroView === 'faq' && (
          <div className="landing-intro-faq-view">
            <div className="landing-intro-about-top">
              <div className="landing-intro-about-brand-inner">
                <button
                  type="button"
                  className="landing-intro-logo-wrap landing-intro-logo-wrap--home landing-intro-logo-wrap--about"
                  onClick={backToDefault}
                  title="Return to welcome message"
                >
                  <AuthLeftBranding showTagline={false} className="landing-intro-auth-logo" />
                </button>
                <p className="landing-intro-slogan landing-intro-slogan--about">A Web App Created to Connect Family Members</p>
              </div>
            </div>
            <div className="landing-intro-faq-body">
              <LandingFaqAccordion />
            </div>
          </div>
        )}
      </main>

      <AuthLandingFooter />

      <LandingWelcomeModal isOpen={showWelcomeModal && !isLoggedIn} onClose={() => setShowWelcomeModal(false)} />
    </div>
  )
}

export default Landing
